import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { IOrder } from "../../models/Order.model";
import { IEpcPayment } from "../../models/epcCustomer.model";
import ConfirmationModal from "../ConfirmationModalComponent/ConfirmationModal";
import { PaymentsModal } from "./PaymentModal";
import { setCurrentEpcCustomer } from "../../store/slices/epcSlice";
import { cancelEPCInvoice } from "../../store/slices/einvoiceSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  Ban,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  Image,
  Package,
  ReceiptText,
  X,
} from "lucide-react";
import InvoiceActions from "./InvoiceActions";
import EPCInvoiceModal from "./EPCInvoiceModal";
import EPCInvoiceViewer from "./EPCInvoiceViewer";
import { getEpcInvoiceIds } from "../../utils/epcInvoiceReference";

const cardActionBase =
  "inline-flex h-9 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";
const cardActionStyles = {
  primary: `${cardActionBase} border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 focus-visible:ring-emerald-500`,
  secondary: `${cardActionBase} border-slate-300 bg-white text-slate-700 hover:bg-slate-50 focus-visible:ring-slate-400`,
  danger: `${cardActionBase} border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 focus-visible:ring-rose-400`,
  info: `${cardActionBase} border-sky-700 bg-sky-700 text-white hover:bg-sky-800 focus-visible:ring-sky-500`,
};

type PaymentCardProps = {
  order?: IOrder;
  epcPayment?: IEpcPayment;
  index: number;
  onPaymentApproveReject: () => void;
  onCardClick?: (order: IOrder, e: React.MouseEvent) => void;
  onUpdateStatus?: (id: string, status: string, type: "order" | "epc") => void;
  onViewUtr?: (order: IOrder) => void;
  onViewScreenshot?: (order?: IOrder) => void;
  onViewProofForEpc?: (epcPayment: IEpcPayment) => void;
  onViewGoods?: (order: IOrder) => void;
  onBulkApprovePayments?: (paymentIds: string[], mainPaymentId: string) => void;
  onBulkRejectPayments?: (paymentIds: string[], mainPaymentId: string) => void;
  type: "order" | "epc";
};

const PaymentCard: React.FC<PaymentCardProps> = ({
  order,
  epcPayment,
  index,
  onPaymentApproveReject,
  onCardClick,
  onUpdateStatus,
  onViewUtr,
  onViewScreenshot,
  onViewGoods,
  onViewProofForEpc,

  type,
}) => {
  const [, setShowInvoiceMenu] = useState(false);
  const invoiceMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        invoiceMenuRef.current &&
        !invoiceMenuRef.current.contains(event.target as Node)
      ) {
        setShowInvoiceMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [showRejectConfirmation, setShowRejectConfirmation] = useState(false);
  const [showEpcInvoiceModal, setShowEpcInvoiceModal] = useState(false);
  const [showEpcInvoiceCancelPicker, setShowEpcInvoiceCancelPicker] =
    useState(false);
  const [showEpcInvoiceCancelConfirmation, setShowEpcInvoiceCancelConfirmation] =
    useState(false);
  const [selectedEpcInvoiceId, setSelectedEpcInvoiceId] = useState("");
  const [epcInvoiceCancelError, setEpcInvoiceCancelError] = useState("");
  const cancelEpcInvoiceLoading = useSelector(
    (state: RootState) => state.einvoice.loading.cancelEPCInvoice,
  );
  const registeredCustomers = useSelector(
    (state: RootState) => state.epc.registeredCustomers,
  );
  const currentCustomer = Array.isArray(registeredCustomers)
    ? registeredCustomers.find(
        (customer) => customer._id === epcPayment?.customer,
      )
    : null;

  const [expandedSection, setExpandedSection] = useState<
    "billing" | "shipping" | null
  >(null);

  const dispatch: RootDispatch = useDispatch();
  const getPaymentData = () => {
    if (type === "epc" && epcPayment) {
      return {
        id: epcPayment._id || "",
        status: epcPayment.paymentStatus || "pending",
        customerName: epcPayment.customerName || "Unknown",
        amount: epcPayment?.paymentPaid,
        date: epcPayment.createdAt
          ? new Date(epcPayment.createdAt)
          : new Date(),
        // EPC specific data
        mobileNumber: epcPayment.customerMobileNumber || "",
        salesRep: epcPayment.salesRepresentative || "",
        totalBill: epcPayment.totalBillAmount || 0,
        address: currentCustomer?.productRegistration?.address || "",
        submittedVia: epcPayment.submittedVia || "",
        hasScreenshot: !!epcPayment.paymentProofUrl,
        payments: epcPayment.payments || [],
        paymentApproved: epcPayment.paymentApproved || 0,
        quote: epcPayment.quote,
      };
    } else if (type === "order" && order) {
      return {
        id: order._id || "",
        status: order.orderStatus || "pending",
        customerName: order.customerId?.name || "Unknown",
        amount: order.totalAmount || 0,
        date: new Date(order.createdDate || Date.now()),
        orderNumber: order.orderNumber || "-",
        transportPrice: order.transportPrice || 0,
        discountPrice: order.discountPrice || 0,
        salesRep: order.salesRep,
        quotationNumber: order.quotationNumber,
        gstin: order.gstin || "",
        // Order specific data
        customerEmail: order.customerId?.email || "",
        customerPhone: order.customerId?.mobileNumber || "",
        hasUtr: !!order.paymentDetails?.utrNumber,
        hasScreenshot: !!order.paymentDetails?.paymentScreenshot,
        orderItems: order.goods?.length || 0,
        billingAddress:
          order.billingAddress || order.customerId?.billingAddress,
        shippingAddress:
          order.shippingAddress || order.customerId?.shippingAddress,
        payments: [],
      };
    }
    return null;
  };

  const data = getPaymentData();
  if (!data) return null;

  const getStatusColor = () => {
    switch (data.status) {
      case "approved":
        return "bg-green-100 text-green-700 border-green-200";
      case "rejected":
        return "bg-red-100 text-red-700 border-red-200";
      default:
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (order) {
      onCardClick?.(order, e);
    }
  };

  const handleConfirmReject = () => {
    onUpdateStatus?.(data.id, "rejected", "order");
    setShowRejectConfirmation(false);
  };

  const renderAddressBlock = (
    title: string,
    type: "billing" | "shipping",
    addr: any,
  ) => {
    const isOpen = expandedSection === type;
    const line2 = addr?.line2 || addr?.address;

    return (
      <div className="border border-gray-200 rounded-lg">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpandedSection(isOpen ? null : type);
          }}
          aria-expanded={isOpen}
          className="flex min-h-9 w-full items-center justify-between rounded-md px-3 text-left text-xs font-semibold text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-inset"
        >
          <span>{title} Address</span>
          <span className="text-gray-400 text-xs">
            {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </span>
        </button>

        {isOpen && addr && (
          <div className="px-3 pb-3 text-xs border-t border-gray-100 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-500">Line 1</span>
              <span className="text-gray-800 text-right">{addr.line1}</span>
            </div>

            {line2 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Line 2</span>
                <span className="text-gray-800 text-right">{line2}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-500">City</span>
              <span className="text-gray-800">{addr.city}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">State</span>
              <span className="text-gray-800">{addr.state}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-gray-500">Pincode</span>
              <span className="text-gray-800">{addr.pincode}</span>
            </div>
          </div>
        )}

        {isOpen && !addr && (
          <div className="px-3 pb-3 text-xs text-gray-400 border-t border-gray-100">
            No address available
          </div>
        )}
      </div>
    );
  };

  const renderOrderDetails = () => {
    const orderData = data as any;
    const createdByAndAuthorSame = Boolean(
      order?.createdBy &&
      order?.author &&
      (order.createdBy._id === order.author._id ||
        order.createdBy.username === order.author.username),
    );
    const orderTeam = [
      { label: "Created by", person: order?.createdBy },
      { label: "Author", person: order?.author },
      { label: "Sales rep", person: order?.salesRep },
      { label: "CSE", person: order?.cse },
    ].filter(
      ({ label, person }) =>
        person?.username && !(label === "Author" && createdByAndAuthorSame),
    );

    return (
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs">
        {orderTeam.length > 0 && (
          <div className="col-span-2 flex flex-wrap gap-x-3 gap-y-1 rounded-md bg-gray-50 px-2 py-1.5">
            {orderTeam.map(({ label, person }) => (
              <div key={label} className="flex min-w-0 items-baseline gap-1">
                <span className="shrink-0 text-[10px] text-gray-500">
                  {label}:
                </span>
                <span className="max-w-[130px] truncate text-[11px] font-medium text-gray-800">
                  {person?.username}
                </span>
              </div>
            ))}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Customer
          </p>
          <p className="font-semibold text-gray-800 truncate">
            {orderData.customerName}
          </p>
        </div>

        <div className="min-w-0">
          <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Phone
          </p>
          <p className="font-medium text-gray-800 truncate">
            {orderData.customerPhone || "-"}
          </p>
        </div>

        <div className="min-w-0 col-span-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Email
          </p>
          <p className="font-medium text-gray-800 truncate">
            {orderData.customerEmail || "-"}
          </p>
        </div>
        {orderData?.salesRep?.companyName &&
          orderData?.salesRep?.companyName !== "" && (
            <div className="min-w-0 col-span-2">
              <p className="text-gray-400">Company</p>
              <p className="font-medium text-gray-800 truncate">
                {orderData?.salesRep?.companyName}
              </p>
            </div>
          )}

        <div>
          <p className="text-gray-400">Order</p>
          <p className="font-medium text-gray-800">{orderData.orderNumber}</p>
        </div>
        {orderData.quotationNumber && orderData.quotationNumber !== "" && (
          <div>
            <p className="text-gray-400">Quote</p>
            <p className="font-medium text-gray-800">
              {orderData.quotationNumber}
            </p>
          </div>
        )}
        <div>
          <p className="text-gray-400">Items</p>
          <p className="font-medium text-gray-800">{orderData.orderItems}</p>
        </div>
        {orderData.gstin && orderData.gstin !== "" && (
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
              GSTIN
            </p>
            <p className="font-medium text-gray-800">{orderData.gstin}</p>
          </div>
        )}
        <div className="col-span-2 grid grid-cols-1 gap-1.5 pt-1">
          {renderAddressBlock("Billing", "billing", orderData.billingAddress)}
          {renderAddressBlock(
            "Shipping",
            "shipping",
            orderData.shippingAddress,
          )}
        </div>
      </div>
    );
  };

  const renderEpcDetails = () => {
    const epcData = data as any;
    return (
      <>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Customer:</span>
          <span className="text-xs font-medium truncate">
            {epcData?.customerName}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Mobile:</span>
          <span className="text-xs">{epcData?.mobileNumber}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Sales Rep:</span>
          <span className="text-xs truncate">{epcData?.salesRep?.name}</span>
        </div>
        {epcData?.salesRep?.companyName &&
          epcData?.salesRep?.companyName !== "" && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">Company:</span>
              <span className="text-xs truncate">
                {epcData?.salesRep?.companyName}
              </span>
            </div>
          )}
        {Number(epcData?.totalBill) > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Total bill</span>
            <span className="text-xs font-semibold text-slate-900">
              ₹{epcData.totalBill.toLocaleString()}
            </span>
          </div>
        )}
        {Number(epcData?.paymentApproved) > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Approved</span>
            <span className="text-xs font-semibold text-emerald-700">
              ₹{epcData.paymentApproved.toLocaleString()}
            </span>
          </div>
        )}
        {epcData?.quote?.adjustmentAmount &&
          epcData?.quote?.adjustmentAmount > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-500">Adjusted amount:</span>
              <span className="text-xs font-semibold text-red-600">
                ₹{epcData?.quote?.adjustmentAmount?.toLocaleString()}
              </span>
            </div>
          )}
        {epcData?.address && (
          <div className="flex flex-col mt-1">
            <span className="text-xs text-gray-500">Address:</span>
            <p className="text-xs text-gray-800 break-words font-medium">
              {epcData?.address}
            </p>
          </div>
        )}
        {/* <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">Submitted Via:</span>
          <span className="text-xs">{epcData?.submittedVia}</span>
        </div> */}
      </>
    );
  };

  const renderOrderActions = () => {
    const orderData = data as any;
    const showInvoiceActions = !["rejected", "cancelled"].includes(
      data.status.toLowerCase(),
    );

    return (
      <div className="mt-auto grid grid-cols-2 gap-2 pt-2">
        {showInvoiceActions && (
          <InvoiceActions
            order={order}
            type={type}
            className="relative"
            buttonClassName={cardActionStyles.primary}
            onInvoiceGenerated={onPaymentApproveReject}
          />
        )}
        {orderData?.hasUtr && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onViewUtr?.(order!);
            }}
            className={cardActionStyles.secondary}
          >
            <FileText size={14} aria-hidden="true" />
            View UTR
          </button>
        )}

        {orderData?.hasScreenshot && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onViewScreenshot?.(order);
            }}
            className={cardActionStyles.secondary}
          >
            <Image size={14} aria-hidden="true" />
            View File
          </button>
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowRejectConfirmation(true);
          }}
          className={cardActionStyles.danger}
        >
          <X size={14} aria-hidden="true" />
          Reject
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onViewGoods?.(order!);
          }}
          className={cardActionStyles.secondary}
        >
          <Package size={14} aria-hidden="true" />
          View Goods
        </button>
      </div>
    );
  };

  const handleEpcPaymentClick = () => {
    dispatch(
      setCurrentEpcCustomer({
        customerPayment: epcPayment,
        customerData: currentCustomer,
      }),
    );
    setShowPaymentsModal(true);
  };

  const handleCancelEpcInvoice = async () => {
    const invoiceId = getEpcInvoiceIds(selectedEpcInvoiceId)[0];
    if (!epcPayment?._id || !invoiceId) {
      setEpcInvoiceCancelError("Select an invoice to cancel.");
      return;
    }

    setEpcInvoiceCancelError("");
    try {
      const response = await dispatch(
        cancelEPCInvoice({
          invoiceId,
          paymentId: epcPayment._id,
        }),
      ).unwrap();
      if (response.success === false) {
        throw new Error(response.message || "Unable to cancel EPC invoice.");
      }
      setShowEpcInvoiceCancelConfirmation(false);
      setSelectedEpcInvoiceId("");
      onPaymentApproveReject();
    } catch (error) {
      console.error("Failed to cancel EPC invoice:", error);
      setEpcInvoiceCancelError(
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Unable to cancel EPC invoice. Please try again.",
      );
    }
  };

  const requestCancelEpcInvoice = () => {
    const invoiceIds = getEpcInvoiceIds(epcPayment?.invoice?.invoiceId);
    if (invoiceIds.length === 0) return;
    setEpcInvoiceCancelError("");
    if (invoiceIds.length === 1) {
      setSelectedEpcInvoiceId(invoiceIds[0]);
      setShowEpcInvoiceCancelConfirmation(true);
    } else {
      setShowEpcInvoiceCancelPicker(true);
    }
  };

  const renderEpcActions = () => {
    const hasGeneratedInvoice =
      Boolean(epcPayment?.invoice?.generated) ||
      ["GENERATED", "PARTIAL"].includes(epcPayment?.invoice?.status || "");
    const invoiceIds = getEpcInvoiceIds(epcPayment?.invoice?.invoiceId);

    return (
      <div className="mt-auto grid grid-cols-2 gap-2 pt-4">
        {!hasGeneratedInvoice &&
          !["rejected", "cancelled"].includes(data.status.toLowerCase()) && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setShowEpcInvoiceModal(true);
            }}
            className={cardActionStyles.primary}
          >
            <ReceiptText size={14} aria-hidden="true" />
            Generate invoice
          </button>
          )}
        {epcPayment && <EPCInvoiceViewer payment={epcPayment} />}
        {hasGeneratedInvoice && invoiceIds.length > 0 && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              requestCancelEpcInvoice();
            }}
            disabled={cancelEpcInvoiceLoading}
            className={cardActionStyles.danger}
          >
            <Ban size={14} aria-hidden="true" />
            Cancel invoice
          </button>
        )}
        {data.payments && data.payments.length > 0 && (
          <button
            type="button"
            onClick={handleEpcPaymentClick}
            className={cardActionStyles.info}
          >
            <Eye size={14} aria-hidden="true" />
            View Payments ({data.payments.length})
          </button>
        )}

        {data.hasScreenshot && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onViewProofForEpc?.(epcPayment!);
            }}
            className={cardActionStyles.secondary}
          >
            <Image size={14} aria-hidden="true" />
            View Proof
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <motion.div
        onClick={handleCardClick}
        className="flex w-full max-w-[360px] cursor-pointer flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
        whileHover={{ y: -2 }}
      >
        <div className="flex flex-1 flex-col p-3">
          {/* Header */}
          <div className="mb-3 flex items-start justify-between gap-2 border-b border-slate-100 pb-2.5">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-sm font-bold text-gray-900">
                  {type === "epc"
                    ? `EPC Payment #${index + 1}`
                    : `${order?.orderNumber || `#${index + 1}`}`}
                </span>
                <span className="text-[11px] text-slate-500">
                  {data?.date?.toLocaleDateString()}
                </span>
              </div>
            </div>

            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold capitalize border ${getStatusColor()}`}
            >
              {data?.status}
            </span>
          </div>

          {/* Details */}
          <div className="mb-3">
            {type === "order" ? renderOrderDetails() : renderEpcDetails()}
          </div>

          {/* Amount */}
          <div className="mb-3 grid grid-cols-2 gap-2">
            {Number(data?.amount) > 0 && (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2">
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                  Total
                </p>
                <p className="mt-0.5 text-sm font-bold text-slate-900">
                  ₹{data.amount?.toLocaleString()}
                </p>
              </div>
            )}

            {type === "order" ? (
              <>
                <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                    Items
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-slate-900">
                    {(data as any)?.orderItems}
                  </p>
                </div>

                {(data as any)?.transportPrice > 0 && (
                  <div className="rounded-md border border-sky-100 bg-sky-50 px-2.5 py-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-sky-700">
                      Transport
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-sky-900">
                      ₹{(data as any).transportPrice.toLocaleString()}
                    </p>
                  </div>
                )}

                {(data as any)?.discountPrice > 0 && (
                  <div className="rounded-md border border-rose-100 bg-rose-50 px-2.5 py-2">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-rose-700">
                      Discount
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-rose-900">
                      ₹{(data as any).discountPrice.toLocaleString()}
                    </p>
                  </div>
                )}
              </>
            ) : (
              Number((data as any)?.paymentApproved) > 0 && (
                <div className="rounded-md border border-emerald-100 bg-emerald-50 px-2.5 py-2">
                  <p className="text-[10px] font-medium uppercase tracking-wide text-emerald-700">
                    Approved
                  </p>
                  <p className="mt-0.5 text-sm font-semibold text-emerald-900">
                    ₹{(data as any).paymentApproved.toLocaleString()}
                  </p>
                </div>
              )
            )}
          </div>

          {/* Actions */}
          {type === "epc" ? (
            renderEpcActions()
          ) : data?.status !== "approved" ? (
            renderOrderActions()
          ) : (
            <div className="mt-auto grid grid-cols-2 gap-2 pt-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewGoods?.(order!);
                }}
                className={cardActionStyles.secondary}
              >
                <Package size={14} aria-hidden="true" />
                View Goods
              </button>
              <InvoiceActions
                order={order}
                type={type}
                className="relative"
                buttonClassName={cardActionStyles.primary}
                onInvoiceGenerated={onPaymentApproveReject}
              />
            </div>
          )}
        </div>
      </motion.div>

      <PaymentsModal
        isOpen={showPaymentsModal}
        onConfirm={onPaymentApproveReject}
        onClose={() => setShowPaymentsModal(false)}
        onViewProofForEpc={() => onViewProofForEpc?.(epcPayment!)}
      />
      <ConfirmationModal
        isOpen={showRejectConfirmation}
        onClose={() => setShowRejectConfirmation(false)}
        onConfirm={handleConfirmReject}
        title="Reject this order?"
        message={`This will mark order ${order?.orderNumber || ""} as rejected.`}
        confirmText="Reject order"
        cancelText="Keep order"
        type="danger"
      />
      {showEpcInvoiceCancelPicker &&
        createPortal(
          <div
            className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/50 p-4"
            onClick={() => setShowEpcInvoiceCancelPicker(false)}
          >
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="cancel-epc-invoice-title"
              className="flex max-h-[85vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <header className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">
                    Cancel EPC invoice
                  </p>
                  <h2
                    id="cancel-epc-invoice-title"
                    className="mt-1 text-lg font-semibold text-slate-900"
                  >
                    Select an invoice
                  </h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Choose the invoice you want to cancel.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close invoice selector"
                  onClick={() => setShowEpcInvoiceCancelPicker(false)}
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X size={18} />
                </button>
              </header>
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-4">
                {getEpcInvoiceIds(epcPayment?.invoice?.invoiceId).map(
                  (invoiceId, index) => (
                    <button
                      key={invoiceId}
                      type="button"
                      onClick={() => {
                        setSelectedEpcInvoiceId(invoiceId);
                        setShowEpcInvoiceCancelPicker(false);
                        setShowEpcInvoiceCancelConfirmation(true);
                      }}
                      className="flex w-full items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-left transition hover:border-rose-300 hover:bg-rose-50"
                    >
                      <FileText
                        size={18}
                        className="shrink-0 text-rose-700"
                        aria-hidden="true"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">
                          Invoice {index + 1}
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {invoiceId}
                        </span>
                      </span>
                    </button>
                  ),
                )}
              </div>
              <footer className="flex justify-start border-t border-slate-100 bg-slate-50 px-5 py-3">
                <button
                  type="button"
                  onClick={() => setShowEpcInvoiceCancelPicker(false)}
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200"
                >
                  Close
                </button>
              </footer>
            </section>
          </div>,
          document.body,
        )}
      <ConfirmationModal
        isOpen={showEpcInvoiceCancelConfirmation}
        onClose={() => {
          if (!cancelEpcInvoiceLoading) {
            setShowEpcInvoiceCancelConfirmation(false);
            setSelectedEpcInvoiceId("");
            setEpcInvoiceCancelError("");
          }
        }}
        onConfirm={() => void handleCancelEpcInvoice()}
        title="Cancel this EPC invoice?"
        message={
          epcInvoiceCancelError ||
          `This will cancel invoice ${selectedEpcInvoiceId}. This action cannot be undone.`
        }
        confirmText="Cancel invoice"
        cancelText="Keep invoice"
        type="danger"
        isLoading={cancelEpcInvoiceLoading}
      />
      {showEpcInvoiceModal && epcPayment && (
        <EPCInvoiceModal
          payment={epcPayment}
          customer={currentCustomer}
          onClose={() => setShowEpcInvoiceModal(false)}
          onInvoiceGenerated={onPaymentApproveReject}
        />
      )}
    </>
  );
};

export default PaymentCard;
